import React from "react";
import { SectionHeading } from "../../shared/components/SectionHeading";
import { useTranslation } from "../../i18n";

const CheckIcon = ({ className = "text-[#111111]" }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M8.5 12.5L11 15L15.5 9.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const HourglassIcon = ({ className = "text-[#9ca3af]" }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M8 4H16M8 20H16M9 4V8C9 9.65685 10.3431 11 12 11C13.6569 11 15 9.65685 15 8V4M9 20V16C9 14.3431 10.3431 13 12 13C13.6569 13 15 14.3431 15 16V20"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RingSpinner = () => {
  const r = 36;
  const c = 2 * Math.PI * r;
  const arcLen = c * 0.25;
  const dashGap = `${arcLen.toFixed(2)} ${(c - arcLen).toFixed(2)}`;

  return (
    <div
      className="mx-auto flex h-[136px] w-[136px] items-center justify-center rounded-full bg-[#F8F9FA]"
      aria-hidden
    >
      <div
        className="flex h-[118px] w-[118px] items-center justify-center rounded-full bg-[#F8F9FA]"
        style={{
          boxShadow:
            "inset 5px 5px 10px rgba(163, 177, 198, 0.38), inset -5px -5px 12px rgba(255, 255, 255, 0.95)",
        }}
      >
        <div className="relative h-[88px] w-[88px] shrink-0">
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke="#E9ECEF"
              strokeWidth="2.5"
            />
          </svg>
          <svg
            className="absolute inset-0 h-full w-full origin-center animate-spin motion-reduce:animate-none [animation-duration:1.05s] [animation-timing-function:linear]"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke="#000000"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={dashGap}
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

function statusForProgress(progress, t) {
  const checklist = [
    { id: "email", label: t("signup.stepEmail") },
    { id: "keys", label: t("signup.stepSecurity") },
    { id: "finalize", label: t("signup.stepSetup") },
  ];
  const ends = [33, 66, 100];
  return checklist.map((item, i) => {
    const start = i === 0 ? 0 : ends[i - 1];
    const end = ends[i];
    let state = "pending";
    if (progress >= end) state = "done";
    else if (progress >= start) state = "active";
    return { ...item, state };
  });
}

const SignupProcessingView = ({ progress }) => {
  const { t } = useTranslation();
  const rows = statusForProgress(progress, t);
  const pct = Math.min(100, Math.max(0, Math.round(progress)));

  return (
    <div className="text-center">
      <SectionHeading className="mb-3 text-[28px] font-semibold leading-tight tracking-[-0.03em] text-[#111111]">
        {t("signup.processingTitle")}
      </SectionHeading>
      <p className="mb-10 text-[14px] leading-7 text-[#4b5563]">
        {t("signup.processingSubtitle")}
      </p>

      <RingSpinner />

      <div className="mt-10 text-left">
        <div className="mb-2 flex items-center justify-between gap-4">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9ca3af]">
            {t("signup.systemIntegrity")}
          </span>
          <span className="text-[13px] font-semibold tabular-nums text-[#111111]">
            {pct}%
          </span>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-[#ececec]">
          <div
            className="h-full rounded-full bg-[#6b7280] transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>

        <ul className="mt-8 space-y-4" aria-live="polite">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-3 text-left text-[12px] font-semibold uppercase tracking-[0.06em]"
            >
              {row.state === "done" && <CheckIcon />}
              {row.state === "active" && <HourglassIcon />}
              {row.state === "pending" && (
                <span className="inline-flex h-5 w-5 shrink-0 rounded-full border border-[#e5e7eb] bg-[#f9fafb]" />
              )}
              <span
                className={
                  row.state === "pending"
                    ? "text-[#d1d5db]"
                    : row.state === "active"
                      ? "text-[#9ca3af]"
                      : "text-[#374151]"
                }
              >
                {row.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SignupProcessingView;
